<template>
    <!-- Spec 2026-10-14 §5.3: edit the recipient of a 111/112 order with no
         tracking number yet (PUT /orders/recipient/:id, seller mount for
         platform 9). Appended to <body> so it can open over the failures modal. -->
    <el-dialog
        :visible="visible"
        :title="title"
        :append-to-body="true"
        :close-on-click-modal="false"
        width="560px"
        custom-class="bo-recipient-edit-dialog"
        @update:visible="v => $emit('update:visible', v)"
        @closed="onClosed"
    >
        <div data-testid="recipient-edit">
            <div v-if="isCod" class="alert alert-warning py-2 px-3 mb-3" data-testid="recipient-edit-cod">
                <i class="fa fa-exclamation-triangle mr-1"></i>{{ $t("tracking_failure.cod_phone_warning") }}
            </div>

            <div class="form-group mb-3">
                <label class="form-control-label" for="re-name">{{ $t("tracking_failure.f_name") }}</label>
                <el-input id="re-name" v-model="form.recipient_name" maxlength="100" data-testid="re-name"></el-input>
                <div v-if="shownError('recipient_name')" class="re-err">{{ $t(shownError('recipient_name')) }}</div>
            </div>

            <div class="form-group mb-3">
                <label class="form-control-label" for="re-phone">{{ $t("tracking_failure.f_phone") }}</label>
                <el-input
                    id="re-phone"
                    :value="phoneDisplay"
                    inputmode="numeric"
                    placeholder="0xx-xxx-xxxx"
                    :class="{ 're-invalid': !!shownError('recipient_phone') }"
                    data-testid="re-phone"
                    @input="onPhoneInput"
                    @blur="touched.recipient_phone = true"
                ></el-input>
                <div v-if="shownError('recipient_phone')" class="re-err" data-testid="re-phone-err">{{ $t(shownError('recipient_phone')) }}</div>
            </div>

            <div class="form-group mb-3">
                <label class="form-control-label" for="re-addr">{{ $t("tracking_failure.f_address") }}</label>
                <el-input id="re-addr" v-model="form.recipient_address_detail" type="textarea" :rows="2" maxlength="500" data-testid="re-address"></el-input>
                <div v-if="shownError('recipient_address_detail')" class="re-err">{{ $t(shownError('recipient_address_detail')) }}</div>
            </div>

            <div class="form-group mb-2 position-relative">
                <label class="form-control-label" for="re-search">{{ $t("tracking_failure.f_area_search") }}</label>
                <el-input
                    id="re-search"
                    v-model="search"
                    :placeholder="$t('tracking_failure.f_area_search_hint')"
                    prefix-icon="el-icon-search"
                    data-testid="re-area-search"
                    @focus="searchOpen = true"
                    @input="searchOpen = true"
                ></el-input>
                <ul v-show="searchOpen && search && suggestions.length" class="re-suggest">
                    <li
                        v-for="(r, i) in suggestions"
                        :key="i"
                        data-testid="re-area-option"
                        @mousedown.prevent="pickArea(r)"
                    >{{ areaLabel(r) }}</li>
                </ul>
            </div>

            <div class="row">
                <div class="col-6 mb-2">
                    <label class="form-control-label">{{ $t("recipent_district") }}</label>
                    <el-input v-model="form.recipient_address_district" data-testid="re-district"></el-input>
                </div>
                <div class="col-6 mb-2">
                    <label class="form-control-label">{{ $t("recipent_city") }}</label>
                    <el-input v-model="form.recipient_address_city" data-testid="re-city"></el-input>
                </div>
                <div class="col-6 mb-2">
                    <label class="form-control-label">{{ $t("recipent_province") }}</label>
                    <el-input v-model="form.recipient_address_province" data-testid="re-province"></el-input>
                </div>
                <div class="col-6 mb-2">
                    <label class="form-control-label">{{ $t("recipent_zipcode") }}</label>
                    <el-input v-model="form.recipient_zipcode" maxlength="5" inputmode="numeric" data-testid="re-zipcode"></el-input>
                </div>
            </div>
            <div v-if="shownError('recipient_area')" class="re-err">{{ $t(shownError('recipient_area')) }}</div>
            <div v-if="shownError('recipient_zipcode')" class="re-err">{{ $t(shownError('recipient_zipcode')) }}</div>

            <div v-if="serverError" class="alert alert-danger py-2 px-3 mt-3 mb-0" data-testid="re-server-error">{{ serverError }}</div>
        </div>

        <span slot="footer">
            <base-button type="secondary" :disabled="saving" @click="$emit('update:visible', false)">{{ $t("cancel") }}</base-button>
            <base-button type="primary" :disabled="saving" data-testid="re-save" @click="save">
                <i v-if="saving" class="fa fa-spinner fa-spin mr-1"></i>{{ $t("save") }}
            </base-button>
        </span>
    </el-dialog>
</template>

<script>
import { Dialog, Input } from "element-ui";
import { OrderService, SellerService } from "@/resource";
import { packingRoundErrorKey } from "@/utils/sellerPackingRound";
import { addressFieldsFromMaster, filterThaiAddresses, thaiAddressLabel } from "@/utils/thaiAddressSearch";
import {
    formatPhone,
    isCodOrder,
    normalizePhone,
    orderIdOf,
    recipientFormFrom,
    recipientPayload,
    validateRecipient
} from "@/utils/trackingFailure";

export default {
    name: "RecipientEditDialog",
    components: {
        [Dialog.name]: Dialog,
        [Input.name]: Input
    },
    props: {
        visible: { type: Boolean, default: false },
        // An order row or a tracking-failure row (recipient_* + platform_id + id).
        order: { type: Object, default: null },
        // "staff" → /api/v1/orders/recipient/:id · "seller" → the seller mount.
        mode: { type: String, default: "staff" }
    },
    data() {
        return {
            form: recipientFormFrom(null),
            touched: {},
            search: "",
            searchOpen: false,
            saving: false,
            serverError: ""
        };
    },
    computed: {
        orderId() {
            return orderIdOf(this.order);
        },
        platformId() {
            return Number((this.order && this.order.platform_id) || 0);
        },
        title() {
            return this.$t("tracking_failure.edit_title", { id: this.orderId });
        },
        isCod() {
            return isCodOrder(this.order);
        },
        phoneDisplay() {
            // Show digits grouped; a stored non-number ("test") shows as typed.
            const raw = this.form.recipient_phone || "";
            return /^[\d\s\-+().๐-๙]*$/.test(raw) ? formatPhone(raw) : raw;
        },
        errors() {
            return validateRecipient(this.form, this.platformId);
        },
        hasErrors() {
            return Object.keys(this.errors).length > 0;
        },
        addressList() {
            return (this.$store && this.$store.getters["Order/getAddress"]) || [];
        },
        suggestions() {
            return filterThaiAddresses(this.addressList, this.search.trim());
        }
    },
    watch: {
        visible: {
            immediate: true,
            handler(v) {
                if (!v) return;
                this.form = recipientFormFrom(this.order);
                this.search = "";
                this.serverError = "";
                // An invalid stored phone shows its error straight away.
                this.touched = { recipient_phone: !!this.form.recipient_phone };
                if (!this.addressList.length && this.$store) {
                    this.$store.dispatch("Order/listAddress").catch(() => {});
                }
            }
        }
    },
    methods: {
        shownError(field) {
            const key = this.errors[field];
            if (!key) return "";
            return this.touched[field] || this.touched._submit ? key : "";
        },
        onPhoneInput(v) {
            this.touched.recipient_phone = true;
            this.form.recipient_phone = normalizePhone(v).slice(0, 10);
        },
        areaLabel(r) {
            return thaiAddressLabel(r);
        },
        pickArea(r) {
            Object.assign(this.form, addressFieldsFromMaster(r));
            this.search = "";
            this.searchOpen = false;
        },
        onClosed() {
            this.searchOpen = false;
            this.saving = false;
        },
        async save() {
            this.$set(this.touched, "_submit", true);
            if (this.hasErrors || !this.orderId) return;
            this.saving = true;
            this.serverError = "";
            const payload = recipientPayload(this.form);
            try {
                const svc = this.mode === "seller" ? SellerService : OrderService;
                const res = await svc.updateRecipient(this.orderId, payload);
                this.$notify({ type: "success", message: this.$t("tracking_failure.edit_saved") });
                this.$emit("saved", { id: this.orderId, form: payload, response: res && res.data });
                this.$emit("update:visible", false);
            } catch (err) {
                const key = packingRoundErrorKey(err);
                this.serverError = key && this.$te(key) ? this.$t(key) : ((err && err.message) || this.$t("errors.internal"));
            } finally {
                this.saving = false;
            }
        }
    }
};
</script>

<style>
.bo-recipient-edit-dialog .el-dialog__body {
    padding-top: 8px;
}
.bo-recipient-edit-dialog .re-err {
    color: #c0392b;
    font-size: 0.8125rem;
    margin-top: 4px;
}
.bo-recipient-edit-dialog .re-invalid .el-input__inner {
    border-color: #c0392b;
}
.bo-recipient-edit-dialog .re-suggest {
    position: absolute;
    z-index: 10;
    left: 0;
    right: 0;
    max-height: 240px;
    overflow-y: auto;
    margin: 2px 0 0;
    padding: 4px 0;
    list-style: none;
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}
.bo-recipient-edit-dialog .re-suggest li {
    padding: 6px 12px;
    cursor: pointer;
    font-size: 0.875rem;
}
.bo-recipient-edit-dialog .re-suggest li:hover {
    background: #f6f9fc;
}
</style>
